import "./assets/complianceAnnualPlanActivities.css";
import { useMemo, useState } from "react";
import moment from "moment";
import XLSX from "sheetjs-style";
import { useTasksQuery } from "./hooks/useTasksQuery";
import { useTasksGroupByQuarterQuery } from "./hooks/useTasksGroupByQuarterQuery.js";
import Table from "../../common/components/Table.jsx";
import Button from "../../common/components/Button.jsx";
import { saveFile } from "../../common/utils/saveFile.util.js";
import { useComplianceByQuarter } from "./hooks/useComplianceByQuarterQuery.js";

const STATUS_LABELS = {
  pendiente: "Programada",
  completada: "Ejecutada",
};

function TaskCard({ task, icon }) {
  return (
    <div className="task-card">
      <div className="task-card-header">
        <span className="task-card-icon">{icon}</span>
        <span className="task-card-title">{task.title}</span>
        <span className={`task-card-status task-card-status--${task.status}`}>
          {STATUS_LABELS[task.status] || task.status}
        </span>
      </div>
      {task.description && task.description !== task.title && (
        <p className="task-card-description">{task.description}</p>
      )}
      <div className="task-card-details">
        <span className="task-card-detail">
          📅 <strong>Fecha:</strong> {task.date}
        </span>
        {task.numberTrainees != null && (
          <span className="task-card-detail">
            👥 <strong>Personas capacitadas:</strong> {task.numberTrainees}
          </span>
        )}
        {task.peopleOrganization != null && (
          <span className="task-card-detail">
            🏢 <strong>Personas organización:</strong> {task.peopleOrganization}
          </span>
        )}
      </div>
    </div>
  );
}

const columns2 = [
  { header: "Año", accessor: "year" },
  { header: "Trimestre", accessor: "quarter" },
  { header: "Programadas", accessor: "scheduled" },
  { header: "Ejecutadas", accessor: "executed" },
  {
    header: "Cumplimiento",
    accessor: "compliance",
    render: (value) => Number(value).toFixed(2) + "%",
  },
];

const columnsPersonasPorTrimestre = [
  { header: "Año", accessor: "year" },
  {
    header: "Trimestre",
    accessor: "quarter",
    render: (value) => quarterLabelForExport(value),
  },
  {
    header: "Total capacitados",
    accessor: "totalTrained",
    render: (value) => (value == null || value === "" ? "0" : value),
  },
  {
    header: "Total organización",
    accessor: "totalOrganization",
    render: (value) => (value == null || value === "" ? "0" : value),
  },
];

const RIGHT_TYPE_EXPORT_LABELS = {
  work: "Plan anual de trabajo",
  training: "Plan anual de capacitación",
  goal: "Meta",
};

const QUARTER_EXPORT_LABELS = {
  1: "T1 (Ene - Mar)",
  2: "T2 (Abr - Jun)",
  3: "T3 (Jul - Sep)",
  4: "T4 (Oct - Dic)",
};

function quarterLabelForExport(quarter) {
  if (quarter === "" || quarter == null) return "";
  const key = Number(quarter);
  return QUARTER_EXPORT_LABELS[key] ?? `T${quarter}`;
}

function buildComplianceExcelAoA({
  compliance,
  totals,
  rightType,
  rightYear,
  rightQuarter,
}) {
  const typeLabel = rightType
    ? RIGHT_TYPE_EXPORT_LABELS[rightType] ?? rightType
    : "Todos los tipos";
  const yearLabel = rightYear ? String(rightYear) : "Todos los años";
  const quarterLabel = rightQuarter
    ? quarterLabelForExport(rightQuarter)
    : "Todos los trimestres";

  const headerRow = ["#", "Año", "Trimestre", "Programadas", "Ejecutadas", "Cumplimiento (%)"];
  const dataRows = compliance.map((row, idx) => [
    idx + 1,
    row.year,
    quarterLabelForExport(row.quarter),
    Number(row.scheduled) || 0,
    Number(row.executed) || 0,
    row.compliance == null ? "" : Number(row.compliance).toFixed(2),
  ]);

  const totalComplianceCell =
    totals.compliancePct == null ? "—" : Number(totals.compliancePct).toFixed(2);

  const totalRow = ["", "Total", "", totals.scheduled, totals.executed, totalComplianceCell];

  return [
    ["Cumplimiento de actividades por trimestre"],
    ["Filtros aplicados"],
    ["Tipo", typeLabel],
    ["Año", yearLabel],
    ["Trimestre", quarterLabel],
    [],
    headerRow,
    ...dataRows,
    totalRow,
  ];
}

function ComplianceAnnualPlanActivities() {
  // Filtros panel izquierdo (Detalle de Actividades)
  const [leftStatus, setLeftStatus] = useState("");
  const [leftStartDate, setLeftStartDate] = useState("");
  const [leftEndDate, setLeftEndDate] = useState("");

  // Filtros panel derecho (Indicadores de Cumplimiento)
  const currentYear = new Date().getFullYear();
  const [rightYear, setRightYear] = useState("");
  const [rightQuarter, setRightQuarter] = useState("");
  const [rightType, setRightType] = useState("");

  const [detailYear, setDetailYear] = useState("");
  const [detailQuarter, setDetailQuarter] = useState("");
  const [detailStatus, setDetailStatus] = useState("");

  const leftFilters = {
    ...(leftStartDate && { startDate: leftStartDate }),
    ...(leftEndDate && { endDate: leftEndDate }),
  };

  const trainingsFilter = { type: "training", ...leftFilters };
  const workFilter = { type: "work", ...leftFilters };

  const showScheduled = !leftStatus || leftStatus === "pendiente";
  const showCompleted = !leftStatus || leftStatus === "completada";

  const { tasks: scheduledTrainings } = useTasksQuery(
    showScheduled ? { ...trainingsFilter, status: "pendiente" } : { ...trainingsFilter, status: "__none__" }
  );
  const { tasks: completedTrainings } = useTasksQuery(
    showCompleted ? { ...trainingsFilter, status: "completada" } : { ...trainingsFilter, status: "__none__" }
  );
  const { tasks: scheduledWork } = useTasksQuery(
    showScheduled ? { ...workFilter, status: "pendiente" } : { ...workFilter, status: "__none__" }
  );
  const { tasks: completedWork } = useTasksQuery(
    showCompleted ? { ...workFilter, status: "completada" } : { ...workFilter, status: "__none__" }
  );

  const rightFilters = {
    ...(rightYear && { year: rightYear }),
    ...(rightQuarter && { quarter: rightQuarter }),
    ...(rightType && { type: rightType }),
  };
  const { compliance } = useComplianceByQuarter(rightFilters);

  const detailQuarterFilters = {
    type: "training",
    ...(detailYear && { year: detailYear }),
    ...(detailQuarter && { quarter: detailQuarter }),
    ...(detailStatus && { status: detailStatus }),
  };
  const {
    tasks: tasksPersonasPorTrimestre,
    loading: loadingPersonasPorTrimestre,
  } = useTasksGroupByQuarterQuery(detailQuarterFilters);

  const complianceTotals = useMemo(() => {
    let scheduled = 0;
    let executed = 0;
    for (const row of compliance) {
      scheduled += Number(row.scheduled) || 0;
      executed += Number(row.executed) || 0;
    }
    const compliancePct =
      scheduled === 0 ? null : (executed / scheduled) * 100;
    return { scheduled, executed, compliancePct };
  }, [compliance]);

  const personasPorTrimestreTotals = useMemo(() => {
    let totalTrained = 0;
    let totalOrganization = 0;
    for (const row of tasksPersonasPorTrimestre) {
      totalTrained += Number(row.totalTrained) || 0;
      totalOrganization += Number(row.totalOrganization) || 0;
    }
    return { totalTrained, totalOrganization };
  }, [tasksPersonasPorTrimestre]);

  const handleExportComplianceExcel = () => {
    const aoa = buildComplianceExcelAoA({
      compliance,
      totals: complianceTotals,
      rightType,
      rightYear,
      rightQuarter,
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = { Sheets: { Cumplimiento: ws }, SheetNames: ["Cumplimiento"] };
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const excelBuffer = XLSX.write(wb, { booktype: "xlsx", type: "array" });
    const file = new Blob([excelBuffer], { type: fileType });
    const filename = `cumplimiento-plan-anual-trimestres_${moment().format(
      "YYYY-MM-DD_HHmm"
    )}.xlsx`;
    saveFile(file, filename);
  };

  const complianceTableFooter = (
    <tr className="tabla-totals-row">
      <td />
      <td>
        <strong>Total</strong>
      </td>
      <td />
      <td>
        <strong>{complianceTotals.scheduled}</strong>
      </td>
      <td>
        <strong>{complianceTotals.executed}</strong>
      </td>
      <td>
        <strong>
          {complianceTotals.compliancePct == null
            ? "—"
            : `${complianceTotals.compliancePct.toFixed(2)}%`}
        </strong>
      </td>
    </tr>
  );

  const personasPorTrimestreTableFooter = (
    <tr className="tabla-totals-row">
      <td />
      <td>
        <strong>Total</strong>
      </td>
      <td />
      <td>
        <strong>{personasPorTrimestreTotals.totalTrained}</strong>
      </td>
      <td>
        <strong>{personasPorTrimestreTotals.totalOrganization}</strong>
      </td>
    </tr>
  );

  return (
    <main className="compliance-page">
      <header className="d-flex justify-content-between align-items-center mb-3 px-3 py-2 border-bottom">
        <h1 className="h2 m-0 fw-bold">Metas y cumplimiento</h1>
      </header>

      <div className="compliance-layout">
        {/* PANEL IZQUIERDO: Detalle de Actividades */}
        <div className="compliance-left">
          <h2 className="compliance-panel-title">📋 Detalle de Actividades</h2>

          <div className="compliance-filters">
            <select
              className="compliance-filter-select"
              value={leftStatus}
              onChange={(e) => setLeftStatus(e.target.value)}
            >
              <option value="">Todos los estados</option>
              <option value="pendiente">Programadas</option>
              <option value="completada">Ejecutadas</option>
            </select>
            <input
              type="date"
              className="compliance-filter-input"
              value={leftStartDate}
              onChange={(e) => setLeftStartDate(e.target.value)}
              placeholder="Desde"
            />
            <input
              type="date"
              className="compliance-filter-input"
              value={leftEndDate}
              onChange={(e) => setLeftEndDate(e.target.value)}
              placeholder="Hasta"
            />
          </div>

          {showScheduled && (
            <section className="plan-section">
              <h2 className="section-title">
                📖 Capacitaciones en seguridad vial programadas
              </h2>
              {scheduledTrainings.length > 0 ? (
                <div className="task-card-list">
                  {scheduledTrainings.map((task) => (
                    <TaskCard key={task.id} task={task} icon="🎓" />
                  ))}
                </div>
              ) : (
                <p className="no-data">❌ No hay capacitaciones programadas.</p>
              )}
            </section>
          )}

          {showCompleted && (
            <section className="plan-section">
              <h2 className="section-title">
                🏆 Capacitaciones en seguridad vial ejecutadas
              </h2>
              {completedTrainings.length > 0 ? (
                <div className="task-card-list">
                  {completedTrainings.map((task) => (
                    <TaskCard key={task.id} task={task} icon="✔️" />
                  ))}
                </div>
              ) : (
                <p className="no-data">❌ No hay capacitaciones ejecutadas.</p>
              )}
            </section>
          )}

          {showScheduled && (
            <section className="plan-section">
              <h2 className="section-title">
                📋 Tareas del Plan de Trabajo programadas
              </h2>
              {scheduledWork.length > 0 ? (
                <div className="task-card-list">
                  {scheduledWork.map((task) => (
                    <TaskCard key={task.id} task={task} icon="📌" />
                  ))}
                </div>
              ) : (
                <p className="no-data">❌ No hay tareas del plan de trabajo programadas.</p>
              )}
            </section>
          )}

          {showCompleted && (
            <section className="plan-section">
              <h2 className="section-title">
                🏆 Tareas del Plan de Trabajo ejecutadas
              </h2>
              {completedWork.length > 0 ? (
                <div className="task-card-list">
                  {completedWork.map((task) => (
                    <TaskCard key={task.id} task={task} icon="✔️" />
                  ))}
                </div>
              ) : (
                <p className="no-data">❌ No hay tareas del plan de trabajo ejecutadas.</p>
              )}
            </section>
          )}
        </div>

        {/* PANEL DERECHO: Indicadores de Cumplimiento */}
        <div className="compliance-right">
          <h2 className="compliance-panel-title">📊 Indicadores de Cumplimiento</h2>

          <section className="plan-section compliance-indicator-block">
            <p className="compliance-filter-block-label">
              Filtros — cumplimiento por trimestre (programadas / ejecutadas)
            </p>
            <div className="compliance-filters">
              <select
                className="compliance-filter-select"
                value={rightType}
                onChange={(e) => setRightType(e.target.value)}
              >
                <option value="">Todos los tipos</option>
                <option value="work">Plan anual de trabajo</option>
                <option value="training">Plan anual de capacitación</option>
                <option value="goal">Meta</option>
              </select>
              <select
                className="compliance-filter-select"
                value={rightYear}
                onChange={(e) => setRightYear(e.target.value)}
              >
                <option value="">Todos los años</option>
                {Array.from({ length: 5 }, (_, i) => currentYear - i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select
                className="compliance-filter-select"
                value={rightQuarter}
                onChange={(e) => setRightQuarter(e.target.value)}
              >
                <option value="">Todos los trimestres</option>
                <option value="1">T1 (Ene - Mar)</option>
                <option value="2">T2 (Abr - Jun)</option>
                <option value="3">T3 (Jul - Sep)</option>
                <option value="4">T4 (Oct - Dic)</option>
              </select>
            </div>

            <div className="compliance-table-heading">
              <h2 className="section-title">
                ✅ Cumplimiento de actividades por trimestre
              </h2>
              <div className="compliance-export-btn-wrap">
                <Button type="button" onClick={handleExportComplianceExcel}>
                  Descargar Excel
                </Button>
              </div>
            </div>
            <Table
              columns={columns2}
              data={compliance}
              showIndex
              footer={complianceTableFooter}
              centerContent
            />
          </section>

          <section className="plan-section compliance-indicator-block">
            <h2 className="section-title mb-2">
              Personas capacitadas y de organización por trimestre
            </h2>
            <p className="compliance-filter-block-label compliance-filter-block-label--muted">
              Solo plan anual de capacitación. Filtros de año, trimestre y estado aplican solo a
              esta tabla.
            </p>
            <div className="compliance-filters">
              <select
                className="compliance-filter-select"
                value={detailYear}
                onChange={(e) => setDetailYear(e.target.value)}
              >
                <option value="">Todos los años</option>
                {Array.from({ length: 5 }, (_, i) => currentYear - i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select
                className="compliance-filter-select"
                value={detailQuarter}
                onChange={(e) => setDetailQuarter(e.target.value)}
              >
                <option value="">Todos los trimestres</option>
                <option value="1">T1 (Ene - Mar)</option>
                <option value="2">T2 (Abr - Jun)</option>
                <option value="3">T3 (Jul - Sep)</option>
                <option value="4">T4 (Oct - Dic)</option>
              </select>
              <select
                className="compliance-filter-select"
                value={detailStatus}
                onChange={(e) => setDetailStatus(e.target.value)}
              >
                <option value="">Todos los estados</option>
                <option value="pendiente">Programadas</option>
                <option value="completada">Ejecutadas</option>
              </select>
            </div>
            <Table
              columns={columnsPersonasPorTrimestre}
              data={tasksPersonasPorTrimestre}
              showIndex
              loading={loadingPersonasPorTrimestre}
              footer={personasPorTrimestreTableFooter}
              centerContent
            />
          </section>
        </div>
      </div>
    </main>
  );
}

export default ComplianceAnnualPlanActivities;
